import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Select from "react-select";
import ApiService from "../util/ApiService";
import Swal from "sweetalert2";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import DropdownPortal from "../util/DropdownPortal";
import VideoAttendeeEditModal from "./VideoAttendeeEditModal";

require("./assets/css/Video.css");

const VideoAttendeesPage = () => {
    document.title = "Atendentes de Vídeo | Plantão Rápido";

    const buttonRefs = useRef({});
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [attendees, setAttendees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedAttendee, setSelectedAttendee] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        lastPage: 1
    });

    useEffect(() => {
        fetchDepartments();
    }, []);

    useEffect(() => {
        fetchAttendees();
    }, [selectedDepartment, pagination.current]);

    const fetchDepartments = async () => {
        try {
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/departments/list-department`,
                { per_page: 100, status: 1 },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setDepartments(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Erro ao carregar departamentos:', error);
        }
    };

    const fetchAttendees = async (page = pagination.current) => {
        try {
            setLoading(true);

            const params = {
                page: page,
                per_page: pagination.pageSize
            };

            if (selectedDepartment?.value) {
                params.department_id = selectedDepartment.value;
            }

            if (searchTerm) {
                params.search = searchTerm;
            }

            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/attendees`,
                params,
                true
            );

            if (response.code === 200) {
                const { data, pagination: paginationData } = ApiService.handleApiResponse(response);
                setAttendees(Array.isArray(data) ? data : []);
                if (paginationData) {
                    setPagination(prev => ({
                        ...prev,
                        current: paginationData.current || page,
                        total: paginationData.total || 0,
                        lastPage: paginationData.lastPage || 1
                    }));
                }
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchAttendees(1);
    };

    const handleCreate = () => {
        setSelectedAttendee(null);
        setShowEditModal(true);
    };

    const handleEdit = (attendee) => {
        setSelectedAttendee(attendee);
        setShowEditModal(true);
        setDropdownOpenId(null);
    };

    const handleDelete = async (attendee) => {
        const result = await Swal.fire({
            title: 'Remover atendente?',
            text: `Deseja remover "${attendee.name}" de todos os departamentos?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, remover',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            const response = await ApiService.send(
                "delete",
                `${process.env.REACT_APP_URL_API}video/attendees/${attendee.id}`,
                null,
                true
            );

            if (response.code === 200) {
                Swal.fire('Removido', 'Atendente removido com sucesso.', 'success');
                fetchAttendees();
            }
        } catch (error) {
            ApiService.handleApiError(error);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Atendentes" pageTitle="Vídeo Chamada" />

                    {/* Stats */}
                    <Row className="mb-4">
                        <Col md={4}>
                            <div className="video-stats-card">
                                <div className="stat-value">{pagination.total || attendees.length}</div>
                                <div className="stat-label">Total de Atendentes</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="video-stats-card">
                                <div className="stat-value text-primary">
                                    {departments.length}
                                </div>
                                <div className="stat-label">Departamentos</div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filters */}
                    <Row className="mb-3">
                        <Col md={3}>
                            <div className="form-group">
                                <label>Departamento</label>
                                <Select
                                    options={departments.map(d => ({ value: d.id, label: d.name }))}
                                    value={selectedDepartment}
                                    onChange={(val) => {
                                        setSelectedDepartment(val);
                                        setPagination(prev => ({ ...prev, current: 1 }));
                                    }}
                                    placeholder="Todos os departamentos"
                                    isClearable
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="form-group">
                                <label>Buscar</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Nome ou email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                            <button
                                className="btn btn-primary"
                                onClick={handleSearch}
                            >
                                <i className="fa fa-search me-2"></i> Buscar
                            </button>
                        </Col>
                        <Col md={4} className="d-flex align-items-end justify-content-end">
                            <button
                                className="btn btn-outline-primary me-2"
                                onClick={() => fetchAttendees()}
                                disabled={loading}
                            >
                                <i className="fa fa-refresh me-2"></i> Atualizar
                            </button>
                            <button
                                className="btn btn-success"
                                onClick={handleCreate}
                            >
                                <i className="fa fa-plus me-2"></i> Novo Atendente
                            </button>
                        </Col>
                    </Row>

                    {/* Attendees List */}
                    <div className="list-container">
                        {loading ? (
                            <Skeleton count={5} height={80} />
                        ) : attendees.length > 0 ? (
                            attendees.map((attendee, index) => (
                                <Col lg={12} key={attendee.id || index}>
                                    <Card className="mb-2 p-2 shadow-sm">
                                        <CardBody className="d-flex justify-content-between align-items-center p-3">
                                            <div className="d-flex align-items-center">
                                                <div className="video-user-avatar me-3">
                                                    {attendee.name?.charAt(0)?.toUpperCase() || 'A'}
                                                </div>
                                                <div>
                                                    <h5 className="mb-1">{attendee.name}</h5>
                                                    <p className="text-muted mb-1">
                                                        {attendee.email && (
                                                            <span>
                                                                <i className="fa fa-envelope me-1"></i>
                                                                {attendee.email}
                                                            </span>
                                                        )}
                                                    </p>
                                                    {attendee.departments && attendee.departments.length > 0 && (
                                                        <p className="mb-0">
                                                            {attendee.departments.map((dept, i) => (
                                                                <span key={dept.id || i} className="badge bg-primary me-1">
                                                                    {dept.name}
                                                                </span>
                                                            ))}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="text-center">
                                                <span className="badge bg-success">
                                                    <i className="fa fa-headset me-1"></i>
                                                    Atendente
                                                </span>
                                            </div>

                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    ref={(el) => (buttonRefs.current[attendee.id] = el)}
                                                    onClick={() => setDropdownOpenId(dropdownOpenId === attendee.id ? null : attendee.id)}
                                                >
                                                    <i className="fa fa-ellipsis-v"></i> Ações
                                                </button>

                                                <DropdownPortal
                                                    parentRef={{ current: buttonRefs.current[attendee.id] }}
                                                    show={dropdownOpenId === attendee.id}
                                                    onClose={() => setDropdownOpenId(null)}
                                                >
                                                    <ul className="dropdown-menu show">
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => handleEdit(attendee)}
                                                            >
                                                                <i className="fa fa-edit me-2"></i> Editar
                                                            </button>
                                                        </li>
                                                        <li><hr className="dropdown-divider" /></li>
                                                        <li>
                                                            <button
                                                                className="dropdown-item text-danger"
                                                                onClick={() => {
                                                                    handleDelete(attendee);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                <i className="fa fa-trash me-2"></i> Remover
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </DropdownPortal>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))
                        ) : (
                            <div className="video-empty-state">
                                <i className="fa fa-headset"></i>
                                <h5 className="text-muted">Nenhum atendente encontrado</h5>
                                <p className="text-muted">
                                    Crie novos atendentes para gerenciar os atendimentos por vídeo.
                                </p>
                                <button className="btn btn-success mt-3" onClick={handleCreate}>
                                    <i className="fa fa-plus me-2"></i> Criar Atendente
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {pagination.lastPage > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <nav>
                                <ul className="pagination">
                                    <li className={`page-item ${pagination.current === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                                            disabled={pagination.current === 1}
                                        >
                                            Anterior
                                        </button>
                                    </li>
                                    {[...Array(pagination.lastPage)].map((_, index) => (
                                        <li
                                            key={index}
                                            className={`page-item ${pagination.current === index + 1 ? 'active' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => setPagination(prev => ({ ...prev, current: index + 1 }))}
                                            >
                                                {index + 1}
                                            </button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${pagination.current === pagination.lastPage ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                                            disabled={pagination.current === pagination.lastPage}
                                        >
                                            Próximo
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}

                    {showEditModal && (
                        <VideoAttendeeEditModal
                            show={showEditModal}
                            onHide={() => {
                                setShowEditModal(false);
                                setSelectedAttendee(null);
                            }}
                            attendee={selectedAttendee}
                            departments={departments}
                            onSave={fetchAttendees}
                        />
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default VideoAttendeesPage;
